import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  FacilityUserList,
  FacilityUserMetaData,
  FacilityUserData,
  EmailCheckResult,
  UnBlockPayload,
} from '../services/FacilityUserService';
import {
  getFacilityUserList,
  getFacilityUserMetaData,
  saveOrUpdateFacilityUser,
  getFacilityUserDetailsById,
  checkEmailUniq,
  checkUsernameUniq,
  unBlockFacilityUser,
} from '../services/FacilityUserService';

export interface FacilityUserListQueryPerams {
  userId: string;
  page?: number;
  size?: number;
  sortField?: string;
  sortType?: string;
  searchTerm?: string;
  facilityId?: number[];
  statusId?: string;
  roleId?: number[];
}

export const useGetFacilityUserList = ({
  page = 0,
  size = 10,
  sortField,
  sortType,
  searchTerm,
  facilityId,
  statusId,
  roleId,
}: FacilityUserListQueryPerams) => {
  const { data, isLoading, isError, error, refetch } =
    useQuery<FacilityUserList>({
      queryKey: [
        'FacilityUserListResponse',
        page,
        size,
        sortField,
        sortType,
        searchTerm,
        facilityId,
        statusId,
        roleId,
      ],
      queryFn: () =>
        getFacilityUserList({
          page,
          size,
          sortField,
          sortType,
          searchTerm,
          facilityId,
          statusId,
          roleId,
        }),
      staleTime: 0,
      gcTime: 0,
    });

  return {
    FacilityUserListResponse: data,
    isLoading,
    isError,
    error,
    refetch,
  };
};

export const useGetFacilityUserMetaData = (userId: string) => {
  const { data, isLoading, isError, error, refetch } =
    useQuery<FacilityUserMetaData>({
      queryKey: ['FacilityUserMetaData', userId],
      queryFn: async () => {
        const data = await getFacilityUserMetaData(userId);
        return data;
      },
      staleTime: 0,
      gcTime: 0,
    });

  return {
    facilityUserMetaData: data,
    isLoading,
    isError,
    error,
    refetch,
  };
};

export const useSaveOrUpdateFacility = (onSuccess: () => void) => {
  const queryClient = useQueryClient();
  return useMutation<
    FacilityUserData,
    Error,
    FacilityUserData & { userId: string; action: string }
  >({
    mutationFn: async ({ userId, action, ...payload }) => {
      const data = await saveOrUpdateFacilityUser(userId, action, payload);
      return data;
    },

    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['FacilityUserListResponse'],
      });
      onSuccess();
    },
    onError: (error) => {
      console.error('Error saving or updating facility user:', error);
    },
  });
};

export const useGetFacilityUserDetailsById = (
  userId: string,
  facilityUserId: number
) => {
  const { data, isLoading, isError, error, refetch } =
    useQuery<FacilityUserData>({
      queryKey: ['FacilityUserDetailsById', userId, facilityUserId],
      queryFn: async () => {
        const data = await getFacilityUserDetailsById(userId, facilityUserId);
        return data;
      },
      staleTime: 0,
      gcTime: 0,
      enabled: !!facilityUserId,
    });

  return {
    FacilityUserDetailsById: data,
    isLoading,
    isError,
    error,
    refetch,
  };
};

export const useCheckEmailUnique = (
  primaryEmail: string,
  facilityUserId?: number
) => {
  const { data, isLoading, isError, error, refetch } =
    useQuery<EmailCheckResult>({
      queryKey: ['FacilityUserUniqEmail', primaryEmail, facilityUserId],
      queryFn: async () => {
        const data = await checkEmailUniq(primaryEmail, facilityUserId);
        return data;
      },
      staleTime: 0,
      gcTime: 0,
      enabled: !!primaryEmail,
    });

  return {
    FacilityUserUniqEmail: data,
    isLoading,
    isError,
    error,
    refetch,
  };
};

export const useCheckUsernameUniq = (
  username: string,
  facilityUserId?: number
) => {
  const { data, isLoading, isError, error, refetch } =
    useQuery<EmailCheckResult>({
      queryKey: ['UserNameUniq', username, facilityUserId],
      queryFn: async () => {
        const data = await checkUsernameUniq(username, facilityUserId);
        return data;
      },
      staleTime: 0,
      gcTime: 0,
      enabled: !!username,
    });

  return {
    UserNameUniq: data,
    isLoading,
    isError,
    error,
    refetch,
  };
};

export const useUnBlockFacilityUser = (onSuccess: () => void) => {
  const queryClient = useQueryClient();
  return useMutation<
    UnBlockPayload,
    Error,
    UnBlockPayload & { userId: string }
  >({
    mutationFn: async ({ userId, ...payload }) => {
      const data = await unBlockFacilityUser(userId, payload);
      return data;
    },

    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['UnBlockFacilityUserRespone'],
      });
      onSuccess();
    },
    onError: (error) => {
      console.error('Error saving or updating facility user:', error);
    },
  });
};
